import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import TicketChats from '../TicketChats';
import Instructions from '../Instructions';
import ApprovalCard from './ApprovalCard';
import {
  whereItStands, kindOf, authorRole, authorName, stamp, ago, statusOf
} from './ticketView';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const MAX_BYTES = 10 * 1024 * 1024;
const FIRST_SHOWN = 15;

/**
 * THE CONVERSATION, which is what every role opens a ticket for.
 *
 * Gordon's decision 5: newest at the TOP, as it has always been, so the
 * reply box sits above the list rather than pinned at the bottom - and every
 * line now says who wrote it and when (the server always returned the
 * names; the old page never drew them).
 *
 * Above the reply box, whatever is waiting on THIS person: the approver's
 * decision card, or the requester's "Yes, it's fixed / Still broken" once IT
 * has marked it resolved, with a plain "where it stands" line for them.
 *
 * Staff see one timeline with filters - messages, calls and visits, changes
 * - built from the comments and the ticket's activity. A contractor, who is
 * not served the activity, sees the messages.
 */
export default function ConversationTab() {
  const ctx = useOutletContext();
  const { ticket, summary, role, can, auth, id, locked, composer, answer, busy, setNotice, contractorMode } = ctx;
  const location = useLocation();
  const plain = role === 'requester' || role === 'raiser';
  const staff = !contractorMode;

  const [comments, setComments] = useState(null);
  const [activity, setActivity] = useState(null);
  const [fields, setFields] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [problem, setProblem] = useState(null);

  const loadComments = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/tickets/${id}/comments`, auth);
      setComments(Array.isArray(r.data) ? r.data : []);
    } catch {
      setComments([]);
    }
  }, [id, auth]);

  const loadActivity = useCallback(async () => {
    if (!staff) { setActivity([]); return; }
    try {
      const r = await axios.get(`${API_BASE}/tickets/${id}/activity`);
      setActivity(Array.isArray(r.data) ? r.data : []);
    } catch {
      setActivity([]);
    }
  }, [id, staff]);

  useEffect(() => { loadComments(); loadActivity(); }, [loadComments, loadActivity]);
  useEffect(() => {
    let live = true;
    axios.get(`${API_BASE}/tickets/${id}/custom-field-values`, auth)
      .then((r) => {
        if (!live) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setFields(list.filter((f) => String(f.value ?? '').trim() !== ''));
      })
      .catch(() => { if (live) setFields([]); });
    return () => { live = false; };
  }, [id, auth]);

  // Arriving from the header: focus the reply box, the decision card, or the
  // "what is still wrong" box.
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    const st = location.state || {};
    // The box appears once the conversation has loaded - later than 30ms on
    // a slow connection - so keep trying for up to two seconds.
    const focusWhenThere = (find) => {
      let tries = 0;
      const tick = () => {
        const el = find();
        if (el) el.focus();
        else if (tries++ < 40) timer = setTimeout(tick, 50);
      };
      let timer = setTimeout(tick, 30);
      return () => clearTimeout(timer);
    };
    const stops = [];
    if (st.focusReply) stops.push(focusWhenThere(() => composer.current));
    if (st.decide) stops.push(focusWhenThere(() => document.getElementById('tk-decide')));
    if (st.broken) setBroken(true);
    return () => stops.forEach((stop) => stop());
  }, [location.state, composer]);

  /* ------------------------------------------------- the timeline */
  const lines = useMemo(() => {
    const out = [];
    for (const c of comments || []) out.push({ ...c, kind: 'message', at: c.created_at });
    for (const a of activity || []) {
      const k = kindOf(a);
      if (k === 'message') continue;            // the comments above are the authority on messages
      out.push({ ...a, kind: k, at: a.created_at });
    }
    out.sort((a, b) => new Date(b.at) - new Date(a.at));
    return out;
  }, [comments, activity]);
  const shown = lines.filter((l) => filter === 'all'
    || (filter === 'messages' && l.kind === 'message')
    || (filter === 'calls' && (l.kind === 'call' || l.kind === 'visit'))
    || (filter === 'changes' && l.kind === 'change'));
  const visible = showAll ? shown : shown.slice(0, FIRST_SHOWN);
  const hidden = shown.length - visible.length;
  const counts = {
    messages: lines.filter((l) => l.kind === 'message').length,
    calls: lines.filter((l) => l.kind === 'call' || l.kind === 'visit').length,
    changes: lines.filter((l) => l.kind === 'change').length
  };

  const posted = (c) => {
    setComments((list) => [c, ...(list || []).filter((x) => x.id !== c.id)]);
    ctx.loadSummary();
  };

  const stands = plain ? whereItStands(ticket, summary) : null;
  const s = statusOf(ticket);
  const waitingOnMe = role === 'requester' && s === 'resolved' && !summary?.answer?.answer;
  const canReply = !(contractorMode && locked);
  const others = [ticket.requester_name, ticket.assigned_to_name, ticket.contractor_name, ticket.approver_name]
    .filter(Boolean).filter((n, i, a) => a.indexOf(n) === i)
    .filter((n) => n !== (ctx.me?.name || '') && n !== (ctx.contractor?.name || ''))
    .map((n) => n.split(/\s+/)[0]);

  return (
    <div className="tk-conv">
      {can.canDecide && <ApprovalCard ctx={ctx} />}

      {stands && (
        <div className="tk-stands" data-tone={stands.tone}>
          <strong>Where it stands:</strong> {stands.text}
        </div>
      )}

      {waitingOnMe && (
        <ResolvedAsk broken={broken} setBroken={setBroken} answer={answer} busy={busy} />
      )}

      {/* Steps IT has sent the requester to do on their own device ("Install
          now", then "Done" or "I need help"). They have no Device tab, so it
          is here, with whatever else is waiting on them; the panel draws
          nothing when nothing has been sent. */}
      {role === 'requester' && ticket.asset && <Instructions ticketId={ticket.id} />}

      <section className="tk-card tk-desc" aria-label="What was asked">
        <p className="tk-desc-by">
          {ticket.requester_name ? `${ticket.requester_name} wrote` : 'Raised'} · {stamp(ticket.created_at)}
        </p>
        <p className="tk-desc-text">{ticket.description || <span className="tk-quiet">No description.</span>}</p>
        {fields.length > 0 && (
          <ul className="tk-field-chips" aria-label="Extra fields">
            {fields.map((f) => (
              <li key={f.custom_field_id || f.id || f.field_label} className="tk-field-chip">
                <span className="tk-field-k">{f.field_label || f.field_name}:</span> {String(f.value)}
              </li>
            ))}
          </ul>
        )}
      </section>

      {problem && <p className="tk-banner" data-tone="red" role="alert">{problem}</p>}

      {canReply ? (
        <Composer ctx={ctx} others={others} onPosted={posted} setProblem={setProblem}
                  onFiles={() => { ctx.loadSummary(); setNotice('File added to the ticket.'); }} />
      ) : (
        <p className="tk-banner" data-tone="grey">This job is settled, so messages are closed. Ask to reopen it from the header if something needs changing.</p>
      )}

      {staff && lines.length > 0 && (
        <div className="tk-filters" role="group" aria-label="Show">
          {[['all', 'Everything', lines.length], ['messages', 'Messages', counts.messages],
            ['calls', 'Calls & visits', counts.calls], ['changes', 'Changes', counts.changes]].map(([k, label, n]) => (
            <button key={k} type="button" className="tk-filter" aria-pressed={filter === k}
                    onClick={() => { setFilter(k); setShowAll(false); }}>
              {label}{k !== 'all' && n ? <span className="tk-filter-n">{n}</span> : null}
            </button>
          ))}
        </div>
      )}

      {comments === null ? (
        <p className="tk-quiet">Loading the conversation…</p>
      ) : shown.length === 0 ? (
        <p className="tk-empty">
          {filter !== 'all' ? 'Nothing of that kind yet.'
            : plain ? 'Nothing yet. IT will reply here, and you’ll get a notification.'
              : `No messages yet.${ticket.requester_name && !contractorMode ? ` Say hello to ${ticket.requester_name.split(/\s+/)[0]}.` : ''}`}
        </p>
      ) : (
        <ol className="tk-timeline" aria-label="Conversation, newest first">
          {visible.map((l) => <Line key={`${l.kind}:${l.id}`} line={l} ticket={ticket} />)}
        </ol>
      )}
      {hidden > 0 && (
        <button type="button" className="tk-more-lines" onClick={() => setShowAll(true)}>
          Show {hidden} earlier {hidden === 1 ? 'line' : 'lines'}
        </button>
      )}

      {staff && filter === 'calls' && (
        <section className="tk-calls" aria-label="Calls and what was typed in them">
          <TicketChats ticketId={id} />
        </section>
      )}
    </div>
  );
}

/** One line of the timeline. */
function Line({ line, ticket }) {
  if (line.kind === 'message') {
    const who = authorName(line);
    const r = authorRole(line, ticket);
    return (
      <li className={`tk-msg${line.is_private ? ' tk-msg-private' : ''}${line.mine ? ' tk-msg-mine' : ''}`}
          data-kind="message">
        <span className="tk-avatar" data-role={r} aria-hidden="true">{initialsOf(who)}</span>
        <div className="tk-msg-body">
          <div className="tk-msg-head">
            <span className="tk-msg-who">{who}</span>
            <span className="tk-role" data-role={r}>{r}</span>
            <time className="tk-msg-at" dateTime={line.created_at} title={stamp(line.created_at)}>{ago(line.created_at)}</time>
          </div>
          {line.is_private && (
            <p className="tk-private-tag">
              <LockIcon /> Private – only IT and approvers see this
            </p>
          )}
          <p className="tk-msg-text">{line.comment || line.body}</p>
        </div>
      </li>
    );
  }
  const who = line.actor_name && line.actor_name !== 'System' ? line.actor_name : null;
  return (
    <li className="tk-event" data-kind={line.kind}>
      <span className="tk-event-dot" aria-hidden="true" />
      <span className="tk-event-text">
        {line.description || line.reason || 'Updated'}
        {who ? ` · ${who}` : ''}
      </span>
      <time className="tk-event-at" dateTime={line.created_at} title={stamp(line.created_at)}>{ago(line.created_at)}</time>
    </li>
  );
}

const initialsOf = (n) => String(n || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

export const LockIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.5" y="7" width="9" height="6.5" rx="1.2" /><path d="M5.5 7V5a2.5 2.5 0 015 0v2" />
  </svg>
);

/** The reply box: above the list, Ctrl/Cmd+Enter to send, files attach to the ticket. */
function Composer({ ctx, others, onPosted, setProblem, onFiles }) {
  const { id, auth, composer, can, role, contractorMode, locked } = ctx;
  const [text, setText] = useState('');
  const [isPrivate, setPrivate] = useState(false);
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const pick = useRef(null);
  const plain = role === 'requester' || role === 'raiser';
  const audience = plain ? 'IT' : others.length ? others.slice(0, 3).join(', ').replace(/, ([^,]*)$/, ' and $1') : 'everyone on the ticket';

  const upload = async (chosen) => {
    const failed = [];
    const done = [];
    for (const f of chosen) {
      if (f.size > MAX_BYTES) { failed.push(`${f.name} (over 10MB)`); continue; }
      const body = new FormData();
      body.append('file', f);
      try {
        await axios.post(`${API_BASE}/tickets/${id}/attachments`, body, {
          headers: { ...(auth?.headers || {}), 'Content-Type': 'multipart/form-data' }
        });
        done.push(f.name);
      } catch (err) {
        failed.push(`${f.name} (${err.response?.data?.error || 'upload failed'})`);
      }
    }
    if (failed.length) setProblem(`Did not upload: ${failed.join('; ')}`);
    return done;
  };

  const send = async (e) => {
    e?.preventDefault();
    const body = text.trim();
    if ((!body && files.length === 0) || sending) return;
    setSending(true);
    setProblem(null);
    try {
      const added = files.length ? await upload(files) : [];
      if (added.length) onFiles();
      const words = [body, added.length ? `Attached: ${added.join(', ')}` : ''].filter(Boolean).join('\n\n');
      if (words) {
        const r = await axios.post(`${API_BASE}/tickets/${id}/comments`,
          { comment: words, ...(isPrivate ? { private: true } : {}) }, auth);
        onPosted({ ...r.data, mine: true, author_name: r.data?.author_name || ctx.me?.name || ctx.contractor?.name || null });
      }
      setText('');
      setFiles([]);
      setPrivate(false);
    } catch (err) {
      // A 428 is the location gate, explained by the shell's own panel.
      if (err.response?.status !== 428) {
        setProblem(`That message did not send. ${err.response?.data?.error || ''}`.trim());
      }
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send(e);
    if (e.key === 'Escape') e.currentTarget.blur();
  };

  return (
    <form className={`tk-composer${isPrivate ? ' tk-composer-private' : ''}`} onSubmit={send}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); const f = [...(e.dataTransfer?.files || [])]; if (f.length) setFiles((x) => [...x, ...f]); }}>
      {isPrivate && (
        <p className="tk-private-tag tk-private-banner"><LockIcon /> Private note – only IT and approvers see this. {contractorMode ? '' : 'The requester and the contractor never do.'}</p>
      )}
      <label className="tk-sr" htmlFor="tk-reply">Reply</label>
      <textarea id="tk-reply" ref={composer} className="tk-composer-input" rows={3}
                value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey}
                placeholder={isPrivate ? 'A note for IT and approvers…' : `Reply to ${audience}…`} />
      {files.length > 0 && (
        <ul className="tk-attached" aria-label="Files to attach">
          {files.map((f, i) => (
            <li key={`${f.name}:${i}`} className="tk-field-chip">
              {f.name}
              <button type="button" className="tk-chip-x" aria-label={`Remove ${f.name}`}
                      onClick={() => setFiles((x) => x.filter((_, j) => j !== i))}>×</button>
            </li>
          ))}
        </ul>
      )}
      <div className="tk-composer-bar">
        {!(contractorMode && locked) && (
          <>
            <input ref={pick} type="file" multiple hidden
                   onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ''; if (f.length) setFiles((x) => [...x, ...f]); }} />
            <button type="button" className="tk-icon-btn" aria-label="Attach a file" title="Attach a file"
                    onClick={() => pick.current?.click()}>
              <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 7.5l-5.3 5.3a3.2 3.2 0 01-4.5-4.5l5.6-5.6a2.1 2.1 0 013 3l-5.6 5.6a1 1 0 01-1.5-1.5L9.9 4.6" />
              </svg>
            </button>
          </>
        )}
        {can.canWritePrivate && (
          <label className="tk-private-toggle">
            <input type="checkbox" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} />
            <span>Private note</span>
          </label>
        )}
        <span className="tk-composer-hint">
          {isPrivate ? 'IT and approvers only' : 'Everyone on the ticket sees this'} · <kbd className="tk-kbd">Ctrl</kbd>+<kbd className="tk-kbd">Enter</kbd>
        </span>
        <button type="submit" className={`tk-btn ${isPrivate ? 'tk-btn-private' : 'tk-btn-primary'}`}
                disabled={sending || (!text.trim() && files.length === 0)}>
          {sending ? 'Sending…' : isPrivate ? 'Add private note' : 'Send'}
        </button>
      </div>
    </form>
  );
}

/** "IT marked this fixed. Is it?" for the person who raised it. */
function ResolvedAsk({ broken, setBroken, answer, busy }) {
  const [note, setNote] = useState('');
  const box = useRef(null);
  useEffect(() => { if (broken) box.current?.focus(); }, [broken]);
  return (
    <section className="tk-card tk-ask" aria-label="Is it fixed?">
      <h2 className="tk-ask-title">IT has marked this as fixed. Is it?</h2>
      {!broken ? (
        <div className="tk-row">
          <button type="button" className="tk-btn tk-btn-go" disabled={Boolean(busy)} onClick={() => answer(true)}>
            Yes, it’s fixed
          </button>
          <button type="button" className="tk-btn" disabled={Boolean(busy)} onClick={() => setBroken(true)}>
            Still broken
          </button>
        </div>
      ) : (
        <form className="tk-form" onSubmit={(e) => { e.preventDefault(); if (note.trim()) answer(false, note.trim()); }}>
          <label className="tk-label" htmlFor="tk-broken">What is still wrong?</label>
          <textarea id="tk-broken" ref={box} className="tk-input" rows={3} value={note}
                    onChange={(e) => setNote(e.target.value)} placeholder="e.g. It still crashes when I open the big workbook." />
          <div className="tk-row">
            <button type="submit" className="tk-btn tk-btn-primary" disabled={Boolean(busy) || !note.trim()}>Tell IT</button>
            <button type="button" className="tk-btn tk-btn-quiet" onClick={() => setBroken(false)}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}
